/**
 * Order Management — order/item normalization, timeline, dates, and countdown logic.
 */

import {
  ORDER_STATUS_SEQUENCE,
  PICKUP_IN_STORE_SEQUENCE,
  TERMINAL_STATUSES,
  TERMINAL_TRACKING_STATUSES,
} from './orderManagementConfig';
import { distanceAndETA } from '../../utils/distanceMileCalculate';
import { parseGeoLocationToLatLng } from './orderManagementUtils';

export function getStatusStyle(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('delivered') || s.includes('picked in store'))
    return { bg: '#D1FAE5', color: '#065F46' };
  if (s.includes('cancel') || s.includes('damaged') || s.includes('unpicked'))
    return { bg: '#FEE2E2', color: '#991B1B' };
  if (s === 'pending') return { bg: '#FEF3C7', color: '#92400E' };
  if (s.includes('accepted')) return { bg: '#DBEAFE', color: '#1E40AF' };
  if (s.includes('ready')) return { bg: '#FEF9C3', color: '#713F12' };
  if (s.includes('pickup pending')) return { bg: '#FFE4CC', color: '#C2410C' };
  if (s.includes('picked the order')) return { bg: '#EDE9FE', color: '#5B21B6' };
  if (s.includes('door')) return { bg: '#E0F2FE', color: '#0C4A6E' };
  return { bg: '#F3F4F6', color: '#374151' };
}

export function formatPromotionLabel(promo) {
  const s = String(promo || '').trim();
  if (!s || s.toLowerCase() === 'none') return null;
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getItemPromotion(raw) {
  const value = raw?.product_promotion ?? raw?.promotion;
  return formatPromotionLabel(value);
}

export function normalizeExtraToppings(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((t) => ({
    name: t.topping_name ?? t.name ?? '—',
    qty: t.topping_qty ?? t.qty ?? 1,
    price: t.price != null ? Number(t.price) : null,
  }));
}

export function getItemLineTotal(item) {
  const qty = Number(item.qty || 1);
  const base = Number(item.price || 0);
  const toppingsPerUnit = (item.toppings || []).reduce(
    (sum, t) => sum + Number(t.price || 0) * Number(t.qty || 1),
    0
  );
  return (base + toppingsPerUnit) * qty;
}

export function normalizeRegularItems(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((r) => ({
    name: r.item ?? r.name ?? '—',
    qty: r.qty ?? 1,
    price: r.price != null ? Number(r.price) : null,
    notes: null,
    toppings: normalizeExtraToppings(r.extra_toppings),
    promotion: getItemPromotion(r),
  }));
}

export function normalizeMealItems(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((r) => ({
    name: r.item ?? r.name ?? '—',
    qty: r.qty ?? 1,
    price: r.mealItemPrice != null ? Number(r.mealItemPrice) : null,
    notes: r.product_category
      ? String(r.product_category).replace(/_/g, ' ')
      : null,
    toppings: normalizeExtraToppings(r.extra_toppings),
    promotion: getItemPromotion(r),
  }));
}

export function normalizeUpsellItems(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((r) => ({
    name: r.item ?? r.name ?? '—',
    qty: r.qty ?? 1,
    price: r.price != null ? Number(r.price) : null,
    notes: null,
    toppings: normalizeExtraToppings(r.extra_toppings),
    promotion: getItemPromotion(r),
  }));
}

export function normalizeFreeItem(order) {
  const free = order?.standard_delivery_free_item;
  if (!free?.name) return [];
  const name = String(free.name);
  const sizeLabel = free.size ? ` (${free.size})` : '';
  return [
    {
      name: `${name}${sizeLabel}`,
      qty: free.qty ?? 1,
      price: 0,
      notes: 'Included free',
      toppings: [],
      promotion: null,
      isFree: true,
    },
  ];
}

export function normalizeOrderStatus(status) {
  return String(status || '')
    .toLowerCase()
    .trim()
    .replace(/_/g, ' ');
}

export function buildStatusLogMap(order) {
  const logs = Array.isArray(order?.order_logs) ? order.order_logs : [];
  const map = new Map();

  [...logs]
    .filter((l) => l.order_status)
    .sort((a, b) => new Date(a.update_time) - new Date(b.update_time))
    .forEach((l) => {
      const key = normalizeOrderStatus(l.order_status);
      map.set(key, {
        status: l.order_status,
        timestamp: l.update_time,
        user_type: l.user_type ?? null,
        user_email: l.user_email ?? null,
        notes: l.notes ?? null,
      });
    });

  if (!map.has('pending') && order?.create_at) {
    map.set('pending', {
      status: 'pending',
      timestamp: order.create_at,
      user_type: 'system',
      user_email: null,
      notes: 'Order placed',
    });
  }

  return map;
}

export function getOrderStatusSequence(order) {
  const logMap = buildStatusLogMap(order);
  const current = normalizeOrderStatus(order?.internal_order_status);

  const useInStore =
    current.includes('picked in store') ||
    [...logMap.keys()].some((k) => k.includes('picked in store'));

  let sequence = useInStore ? [...PICKUP_IN_STORE_SEQUENCE] : [...ORDER_STATUS_SEQUENCE];

  const terminal = TERMINAL_TRACKING_STATUSES.find(
    (s) => logMap.has(normalizeOrderStatus(s)) || current === normalizeOrderStatus(s)
  );
  if (terminal && !sequence.some((s) => normalizeOrderStatus(s) === normalizeOrderStatus(terminal))) {
    sequence = [...sequence, terminal];
  }

  return sequence;
}

export function buildOrderTimeline(order) {
  const sequence = getOrderStatusSequence(order);
  const logMap = buildStatusLogMap(order);
  const currentNorm = normalizeOrderStatus(order?.internal_order_status);

  const entries = sequence.map((status) => {
    const norm = normalizeOrderStatus(status);
    const log = logMap.get(norm);
    const isComplete = !!log;
    const isCurrent = currentNorm === norm;
    return {
      status: log?.status ?? status,
      timestamp: log?.timestamp ?? null,
      user_type: log?.user_type ?? null,
      user_email: log?.user_email ?? null,
      notes: log?.notes ?? null,
      isComplete,
      isCurrent,
      isPending: !isComplete && !isCurrent,
    };
  });

  return entries.reverse();
}

const PENDING_STATUS_NORM = normalizeOrderStatus('pending');

/**
 * Map tracking timeline — order_logs drive active/complete after pending.
 * Pending is always complete (placed). Latest log = active; earlier logs = complete.
 */
export function buildOrderTimelineFromLogs(order) {
  const sequence = getOrderStatusSequence(order);
  const logMap = buildStatusLogMap(order);

  const rawLogs = (Array.isArray(order?.order_logs) ? order.order_logs : [])
    .filter((l) => l?.order_status)
    .sort((a, b) => new Date(a.update_time) - new Date(b.update_time));

  const loggedNorms = new Set(rawLogs.map((l) => normalizeOrderStatus(l.order_status)));

  const latestNorm = rawLogs.length
    ? normalizeOrderStatus(rawLogs[rawLogs.length - 1].order_status)
    : null;

  const entries = sequence.map((status) => {
    const norm = normalizeOrderStatus(status);
    const log = logMap.get(norm);

    if (norm === PENDING_STATUS_NORM) {
      const pendingLog = logMap.get(PENDING_STATUS_NORM);
      return {
        status: pendingLog?.status ?? 'pending',
        timestamp: pendingLog?.timestamp ?? order?.create_at ?? null,
        user_type: pendingLog?.user_type ?? null,
        user_email: pendingLog?.user_email ?? null,
        notes: pendingLog?.notes ?? null,
        isComplete: true,
        isCurrent: false,
        isPending: false,
      };
    }

    const inLogs = loggedNorms.has(norm);
    const isCurrent = inLogs && norm === latestNorm;
    const isComplete = inLogs && norm !== latestNorm;

    return {
      status: log?.status ?? status,
      timestamp: inLogs ? log?.timestamp ?? null : null,
      user_type: log?.user_type ?? null,
      user_email: log?.user_email ?? null,
      notes: log?.notes ?? null,
      isComplete,
      isCurrent,
      isPending: !inLogs,
    };
  });

  return entries.reverse();
}

export function fmtDate(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function fmtTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** End of delivery_time_slot for "Delivery Before" labels (e.g. "11:00 AM"). */
export function formatDeliveryBeforeTime(slotStr) {
  if (!slotStr || String(slotStr).trim() === '') return null;
  const slotParts = String(slotStr).split(' - ');
  const endTimeStr = (slotParts.length >= 2 ? slotParts[1] : slotParts[0]).trim();
  if (!endTimeStr) return null;
  return endTimeStr.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

/** Start of delivery_time_slot (e.g. "9:00 AM"). */
export function formatSlotStartTime(slotStr) {
  if (!slotStr || String(slotStr).trim() === '') return null;
  const startTimeStr = String(slotStr).split(' - ')[0]?.trim();
  if (!startTimeStr) return null;
  return startTimeStr.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

export function isOrderPickedUp(order) {
  const status = String(order?.internal_order_status ?? '').toLowerCase();
  if (status.includes('picked the order')) return true;
  if (status.includes('at the door')) return true;
  if (status.includes('delivered')) return true;
  if (status.includes('picked in store')) return true;
  if (order?.driver_information?.order_picked_time) return true;
  return false;
}

/** Matches Order Management delivery-type filter (e.g. store pickup, not home delivery). */
export function isPickupDeliveryOrder(order) {
  const type = String(order?.delivery_type ?? '').toLowerCase();
  return type.includes('pickup');
}

export function isOrderDelivered(order) {
  const status = String(order?.internal_order_status ?? '').toLowerCase();
  return status.includes('delivered') || status.includes('picked in store');
}

/** Latest delivered / picked-in-store timestamp from order_logs, then driver_information. */
export function getOrderDeliveredTimestamp(order) {
  const logs = Array.isArray(order?.order_logs) ? order.order_logs : [];
  const terminalLogs = logs
    .filter((l) => {
      const s = normalizeOrderStatus(l.order_status);
      return s.includes('delivered') || s.includes('picked in store');
    })
    .sort((a, b) => new Date(b.update_time) - new Date(a.update_time));

  if (terminalLogs.length) return terminalLogs[0].update_time;

  const driverDelivered = order?.driver_information?.order_delivered_time;
  if (driverDelivered) return driverDelivered;

  return null;
}

export function getOrderDeliveredFooter(order) {
  if (!isOrderDelivered(order)) return null;

  const status = String(order?.internal_order_status ?? '').toLowerCase();
  const time = fmtTime(getOrderDeliveredTimestamp(order));
  const label = status.includes('picked in store') ? 'Picked in store at' : 'Order delivered at';

  return { label, time: time ?? '--' };
}

/** Estimated pickup time for driver-assigned orders not yet picked up. */
export function formatEstimatedPickupTime(order, driver) {
  const candidates = [
    order?.estimated_pickup_time,
    order?.estimated_pickup,
    order?.pickup_time,
    driver?.estimated_pickup_time,
    driver?.estimated_pickup,
    driver?.pickup_time,
    driver?.order_accepted_time,
  ];

  for (const value of candidates) {
    if (value == null || String(value).trim() === '') continue;
    const formatted = fmtTime(value);
    if (formatted) return formatted;
    const raw = String(value).trim();
    if (/am|pm/i.test(raw)) {
      return raw.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
    }
  }

  return formatSlotStartTime(order?.delivery_time_slot);
}

function pickEtaField(eta, ...keys) {
  if (!eta || typeof eta !== 'object') return null;
  for (const key of keys) {
    const value = eta[key];
    if (value != null && value !== '') return value;
  }
  return null;
}

function formatEtaDistance(value) {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(value);
  if (!Number.isNaN(n)) {
    const rounded = Number.isInteger(n) ? String(n) : n.toFixed(1);
    return `${rounded}m`;
  }
  const raw = String(value).trim();
  if (/mi|km|m\b/i.test(raw)) return raw.replace(/\s*mi\b/i, 'm');
  return raw;
}

function formatEtaMinutes(value) {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(value);
  if (!Number.isNaN(n)) return `${Math.round(n)} Min`;
  const raw = String(value).trim();
  if (/min/i.test(raw)) return raw.replace(/\bmin\b/i, 'Min');
  return `${raw} Min`;
}

function segmentFromEtaObject(obj) {
  if (obj == null) return null;
  if (typeof obj === 'string') {
    const parts = obj.split('|').map((p) => p.trim());
    if (parts.length >= 2) {
      return {
        distance: parts[0] || null,
        time: parts[1] || null,
      };
    }
    return null;
  }
  if (typeof obj !== 'object') return null;

  const distance = formatEtaDistance(
    pickEtaField(obj, 'distanceMiles', 'miles', 'straightLineMiles', 'distance', 'distance_miles')
  );
  const time = formatEtaMinutes(
    pickEtaField(obj, 'etaMinutes', 'minutes', 'time', 'duration', 'eta_minutes')
  );

  if (!distance && !time) return null;
  return { distance, time };
}

export function formatRouteSegmentLabel(segment) {
  if (!segment?.distance && !segment?.time) return '--';
  return `${segment.distance ?? '--'} | ${segment.time ?? '--'}`;
}

export function orderHasReadyForPickup(order) {
  return buildStatusLogMap(order).has(normalizeOrderStatus('order ready for pickup'));
}

export function orderHasDriverAssigned(order) {
  const driver = order?.driver_information;
  if (driver?.email != null || driver?.first_name != null) return true;
  return (order?.order_logs ?? []).some((l) => {
    const s = normalizeOrderStatus(l.order_status);
    return (
      s.includes('pickup pending') ||
      s.includes('picked the order') ||
      s.includes('at the door')
    );
  });
}

/** Log with driver_coordinates when driver accepts / pickup pending. */
export function findDriverAssignmentLog(order) {
  const logs = (Array.isArray(order?.order_logs) ? order.order_logs : [])
    .filter(
      (l) =>
        Array.isArray(l?.driver_coordinates) &&
        l.driver_coordinates.length >= 2 &&
        Number.isFinite(Number(l.driver_coordinates[0])) &&
        Number.isFinite(Number(l.driver_coordinates[1]))
    )
    .sort((a, b) => new Date(a.update_time) - new Date(b.update_time));

  const pickupPending = logs.filter((l) =>
    normalizeOrderStatus(l.order_status).includes('pickup pending')
  );
  if (pickupPending.length) return pickupPending[pickupPending.length - 1];
  return logs.length ? logs[logs.length - 1] : null;
}

function driverCoordsToLatLng(coords) {
  const fromGeo = parseGeoLocationToLatLng({ coordinates: coords });
  if (fromGeo) return fromGeo;
  const [a, b] = coords.map(Number);
  const looksLonLat = Math.abs(a) > Math.abs(b) && Math.abs(a) <= 180 && Math.abs(b) <= 90;
  return looksLonLat ? [b, a] : [a, b];
}

/**
 * Driver → kitchen distance/time when kitchen is ready and driver is assigned.
 * Uses driver_coordinates from the assignment order log + kitchen_geolocation.
 */
export function buildDriverToKitchenSegmentFromLogs(order) {
  if (!orderHasReadyForPickup(order)) return null;
  if (!orderHasDriverAssigned(order)) return null;
  if (isOrderPickedUp(order)) return null;

  const assignmentLog = findDriverAssignmentLog(order);
  if (!assignmentLog) return null;

  const kitchenLatLng = parseGeoLocationToLatLng(order?.kitchen_geolocation);
  if (!kitchenLatLng) return null;

  let driverLatLng;
  try {
    driverLatLng = driverCoordsToLatLng(assignmentLog.driver_coordinates);
  } catch {
    return null;
  }

  try {
    const eta = distanceAndETA(driverLatLng, kitchenLatLng);
    return segmentFromEtaObject({
      straightLineMiles: eta.roadMiles,
      etaMinutes: eta.etaMinutes,
    });
  } catch {
    return null;
  }
}

/** Driver → kitchen and kitchen → customer legs from order.ETA (or order.eta). */
export function buildRouteEtaSegments(order) {
  const eta = order?.ETA ?? order?.eta ?? null;
  const driverToKitchenFromLogs = buildDriverToKitchenSegmentFromLogs(order);

  if (!eta || typeof eta !== 'object') {
    return {
      driverToKitchen: driverToKitchenFromLogs,
      kitchenToCustomer: null,
      driverToKitchenLabel: formatRouteSegmentLabel(driverToKitchenFromLogs),
      kitchenToCustomerLabel: '--',
    };
  }

  if (Array.isArray(eta.segments) && eta.segments.length >= 2) {
    let driverToKitchen = segmentFromEtaObject(eta.segments[0]);
    const kitchenToCustomer = segmentFromEtaObject(eta.segments[1]);
    if (driverToKitchenFromLogs) driverToKitchen = driverToKitchenFromLogs;
    return {
      driverToKitchen,
      kitchenToCustomer,
      driverToKitchenLabel: formatRouteSegmentLabel(driverToKitchen),
      kitchenToCustomerLabel: formatRouteSegmentLabel(kitchenToCustomer),
    };
  }

  let driverToKitchen = segmentFromEtaObject(
    pickEtaField(eta, 'driverToKitchen', 'driver_to_kitchen', 'driverToStore', 'driver_to_store', 'pickupLeg', 'leg1')
  );
  let kitchenToCustomer = segmentFromEtaObject(
    pickEtaField(
      eta,
      'kitchenToCustomer',
      'kitchen_to_customer',
      'storeToCustomer',
      'store_to_customer',
      'deliveryLeg',
      'leg2'
    )
  );

  if (!kitchenToCustomer) {
    kitchenToCustomer = segmentFromEtaObject({
      straightLineMiles: pickEtaField(eta, 'straightLineMiles', 'straight_line_miles', 'miles'),
      etaMinutes: pickEtaField(eta, 'etaMinutes', 'eta_minutes', 'minutes'),
    });
  }

  if (!driverToKitchen) {
    driverToKitchen = segmentFromEtaObject({
      straightLineMiles: pickEtaField(eta, 'driverStraightLineMiles', 'driverMiles', 'pickupMiles'),
      etaMinutes: pickEtaField(eta, 'driverEtaMinutes', 'pickupMinutes', 'driverMinutes'),
    });
  }

  if (driverToKitchenFromLogs) {
    driverToKitchen = driverToKitchenFromLogs;
  }

  return {
    driverToKitchen,
    kitchenToCustomer,
    driverToKitchenLabel: formatRouteSegmentLabel(driverToKitchen),
    kitchenToCustomerLabel: formatRouteSegmentLabel(kitchenToCustomer),
  };
}

export function fmtDateTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatStatusLabel(s) {
  return String(s || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const DEDICATED_DRIVER_ACCOUNT_TYPE_LABELS = {
  regionaldriver: 'Regional driver',
  kitchendedicateddriver: 'Kitchen dedicated',
};

/** Human-readable label for dedicated driver account_type (avoids broken wraps on compound strings). */
export function formatDedicatedDriverAccountType(accountType) {
  const key = String(accountType ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (DEDICATED_DRIVER_ACCOUNT_TYPE_LABELS[key]) {
    return DEDICATED_DRIVER_ACCOUNT_TYPE_LABELS[key];
  }
  if (!key) return '—';
  return key
    .replace(/(kitchen)(dedicated)/i, '$1 $2')
    .replace(/(dedicated)(driver)/i, '$1 $2')
    .replace(/(regional)(driver)/i, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getDedicatedDriverAccountTypeStyle(accountType) {
  const key = String(accountType ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (key === 'regionaldriver') {
    return { bg: '#EDE9FE', color: '#5B21B6' };
  }
  return { bg: '#DBEAFE', color: '#1E40AF' };
}

export function calcRemainingTime(order, now) {
  const status = String(order?.internal_order_status ?? '');
  if (TERMINAL_STATUSES.has(status)) return null;

  const dateStr = order?.delivery_date;
  const slotStr = order?.delivery_time_slot;
  if (!dateStr || !slotStr) return null;

  const slotParts = slotStr.split(' - ');
  const endTimeStr = (slotParts.length >= 2 ? slotParts[1] : slotParts[0]).trim();

  const dateParts = dateStr.split('/');
  if (dateParts.length !== 3) return null;
  const [month, day, year] = dateParts;

  const timeMatch = endTimeStr.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!timeMatch) return null;

  let hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2], 10);
  const ampm = timeMatch[3].toLowerCase();
  if (ampm === 'pm' && hours !== 12) hours += 12;
  if (ampm === 'am' && hours === 12) hours = 0;

  const deadline = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    hours,
    minutes,
    0,
    0
  );

  const diffMs = deadline.getTime() - now;
  const totalSeconds = Math.floor(diffMs / 1000);

  return {
    totalSeconds,
    isOverdue: totalSeconds < 0,
    isCritical: totalSeconds < 30 * 60,
  };
}

export function formatRemainingLabel(totalSeconds) {
  const overdue = totalSeconds < 0;
  const abs = Math.abs(totalSeconds);
  const prefix = overdue ? '−' : '';

  if (abs < 3600) {
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return `${prefix}${m}:${String(s).padStart(2, '0')}`;
  }

  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  let label = `${prefix}${h}h ${m}m`;
  if (s > 0) label += ` ${s}s`;
  return label;
}

export function htmlDateToMDY(v) {
  if (!v) return '';
  const [y, m, d] = v.split('-');
  return `${m}/${d}/${y}`;
}

export function mdyToHtmlDate(v) {
  if (!v) return '';
  const parts = v.split('/');
  if (parts.length !== 3) return '';
  const [m, d, y] = parts;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}
